import * as React from 'react';
import { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { action, computed, observable } from "mobx";
import { sortString } from "../utils";
import FilterValue from './FilterValue';
import { translate } from './Translate';
import { DataStore, FilterStore } from '../stores';
import { FilterSearchKeys } from '../stores/FilterStore';

type Filter = {
    type: 'CLSS' | 'DOCUMENT_META' | 'DOCUMENT_PROPERTY';
    property: string;
    label: string;
    values: Array<FilterValue>;
    ui?: string;
    expanded?: boolean;
    enableSearch?: boolean;
    filterSearchKey: FilterSearchKeys;
};

type FilterValue = {
    label: string;
    value: string;
};

interface FilterItemProps {
    theme?: { [property: string]: string | number };
    translations?: { [property: string]: string };
    dataStore?: DataStore;
    filterStore?: FilterStore;
    filter: Filter;
}

// Higher-order component to return a filter with data and methods required to function
const withFilter = (WrappedComponent) => {

    @inject('translations')
    @inject('dataStore')
    @inject('filterStore')
    @observer
    class FilterItem extends Component<FilterItemProps> {
        @observable search: string = '';
        @observable expanded: boolean = this.props.dataStore.config.filtersTop 
            ? false 
            : this.props.filter.hasOwnProperty('expanded') ? this.props.filter.expanded : true;

        @computed
        get filterSearchKey(): FilterSearchKeys {
            return this.props.filter.filterSearchKey;
        }

        @computed
        get values(): Array<FilterValue> {
            return this.props.filter.values || [];
        }

        @computed
        get filteredValues(): Array<FilterValue> {
            return this.values
                .filter(value => value.label.toLowerCase().includes(this.search.toLowerCase()))
                .filter(value => !this.props.filterStore.isChecked(this.props.filter.property, value.value, this.props.filter.filterSearchKey))
                .sort((a, b) => sortString(a.label, b.label));
        }

        @computed
        get filteredCount(): number {
            return this.filteredValues.length + this.selectedValues.length;
        }

        @computed
        get selectedValues(): Array<FilterValue> {
            return this.values
                .filter(value => this.props.filterStore.isChecked(this.props.filter.property, value.value, this.props.filter.filterSearchKey))
                .sort((a, b) => sortString(a.label, b.label));
        }

        @computed
        get filterDropdownText(): string {
            return this.selectedValues.length
                ? this.selectedValues.map(value => translate(this.props.translations, value.label)).join(', ')
                : translate(this.props.translations, 'Select filters');
        }

        @computed
        get enableSearch(): boolean {
            return this.props.filter.enableSearch ? true : false;
        }

        @action
        toggleExpanded = () => {
            this.expanded = !this.expanded;
        }

        @action
        filterSearch = (value: string) => {
            this.search = value;
        }

        @action
        clearSearch = () => {
            this.search = '';
        }

        render() {
            return (
                <WrappedComponent
                    expanded={this.expanded}
                    toggleExpanded={this.toggleExpanded}
                    selectedCount={this.selectedValues.length}
                    total={this.values.length}
                    search={this.search}
                    clearSearch={this.clearSearch}
                    selectedValues={this.selectedValues}
                    filteredValues={this.filteredValues}
                    filterSearch={this.filterSearch}
                    enableSearch={this.enableSearch}
                    filterDropdownText={this.filterDropdownText}
                    {...this.props}
                />
            );
        }
    }

    return FilterItem;
};

export default withFilter;


// WEBPACK FOOTER //
// ./src/Components/withFilter.tsx